import { Edge, MarkerType, Node } from 'reactflow';

import {
  Forge,
  ForgeApplication,
  ForgeClient,
  OrganizationWithUsers,
  User,
} from '../../../types';
import { DiagramNodeData } from './nodes';

export type DiagramKind = 'user' | 'org' | 'client' | 'forge' | 'app';

export interface DiagramGraph {
  nodes: Node<DiagramNodeData>[];
  edges: Edge[];
}

const COL_X: Record<DiagramKind, number> = {
  user: 0,
  org: 300,
  client: 600,
  forge: 900,
  app: 1200,
};
const ROW_HEIGHT = 96;
const BLOCK_GAP = 48;

export const nodeId = (kind: DiagramKind, id: string) => `${kind}:${id}`;

const makeEdge = (source: string, target: string): Edge => ({
  id: `e:${source}->${target}`,
  source,
  target,
  sourceHandle: 'out',
  targetHandle: 'in',
  type: 'smoothstep',
  animated: false,
  markerEnd: { type: MarkerType.ArrowClosed, width: 14, height: 14 },
  style: { stroke: 'hsl(var(--muted-foreground))', strokeWidth: 1.5, opacity: 0.7 },
});

export interface BuildGraphInput {
  clients: ForgeClient[];
  forges: Forge[];
  apps: ForgeApplication[];
  organizations: OrganizationWithUsers[];
  collapsed: Set<string>;
  onToggleCollapse: (nodeId: string) => void;
}

/**
 * Build a hierarchical User -> Org -> ForgeClient -> Forge -> Application graph.
 * Only users whose organization is mapped to a Forge client are included.
 */
export const buildDiagramGraph = (input: BuildGraphInput): DiagramGraph => {
  const { clients, forges, apps, organizations, collapsed, onToggleCollapse } = input;

  const forgesByClient = new Map<string, Forge[]>();
  forges.forEach((f) => {
    const list = forgesByClient.get(f.client_id) || [];
    list.push(f);
    forgesByClient.set(f.client_id, list);
  });

  const appsByForge = new Map<string, ForgeApplication[]>();
  apps.forEach((a) => {
    const list = appsByForge.get(a.forge_id) || [];
    list.push(a);
    appsByForge.set(a.forge_id, list);
  });

  // Orgs mapped to each client, with their users.
  // One Etabli user belongs to exactly one organization, which maps to at most
  // one Forge client — so the resulting graph remains a tree.
  const orgsByClient = new Map<string, OrganizationWithUsers[]>();
  organizations.forEach((org) => {
    if (!org.forge_client_id) return;
    const list = orgsByClient.get(org.forge_client_id) || [];
    list.push(org);
    orgsByClient.set(org.forge_client_id, list);
  });

  const nodes: Node<DiagramNodeData>[] = [];
  const edges: Edge[] = [];

  const connect = (source: string, target: string) => {
    edges.push(makeEdge(source, target));
  };

  const sortedClients = [...clients].sort((a, b) => a.name.localeCompare(b.name));

  let cursorY = 0;

  sortedClients.forEach((client) => {
    const clientNodeId = nodeId('client', client.client_id);
    const clientCollapsed = collapsed.has(clientNodeId);

    const orgsOfClient = [...(orgsByClient.get(client.client_id) || [])].sort((a, b) =>
      a.name.localeCompare(b.name)
    );
    const forgesOfClient = [...(forgesByClient.get(client.client_id) || [])].sort((a, b) =>
      (a.display_name || a.name).localeCompare(b.display_name || b.name)
    );

    // ---------- Compute left-side rows (orgs + their users) ----------
    const leftBlocks = orgsOfClient.map((org) => {
      const users = org.users || [];
      return { org, users, rows: Math.max(1, users.length) };
    });
    const leftRows = leftBlocks.reduce((s, b) => s + b.rows, 0);

    // ---------- Compute right-side rows (forges + their apps) ----------
    let rightRows = 0;
    const forgeAppMeta: Array<{
      forge: Forge;
      appsOfForge: ForgeApplication[];
      forgeCollapsed: boolean;
      rows: number;
    }> = [];
    if (!clientCollapsed) {
      forgesOfClient.forEach((f) => {
        const forgeNodeId = nodeId('forge', f.forge_id);
        const forgeCollapsed = collapsed.has(forgeNodeId);
        const appsOfForge = appsByForge.get(f.forge_id) || [];
        const rows = forgeCollapsed ? 1 : Math.max(1, appsOfForge.length);
        forgeAppMeta.push({ forge: f, appsOfForge, forgeCollapsed, rows });
        rightRows += rows;
      });
      if (forgesOfClient.length === 0) rightRows = 0;
    }

    const blockRows = Math.max(1, leftRows, rightRows);
    const blockTop = cursorY;
    const blockCenter = blockTop + ((blockRows - 1) * ROW_HEIGHT) / 2;

    // ---------- Client node (column 2, vertically centered) ----------
    const forgesCount = forgesOfClient.length;
    const totalAppsCount = forgesOfClient.reduce(
      (s, f) => s + (appsByForge.get(f.forge_id) || []).length,
      0
    );
    nodes.push({
      id: clientNodeId,
      type: 'client',
      position: { x: COL_X.client, y: blockCenter },
      data: {
        kind: 'client',
        label: client.name,
        sublabel: client.quotas
          ? `${client.quotas.cpu} CPU · ${client.quotas.memory} · ${client.quotas.pods} pods`
          : null,
        collapsible: forgesCount > 0,
        collapsed: clientCollapsed,
        hiddenSummary: clientCollapsed
          ? `${forgesCount} forge${forgesCount > 1 ? 's' : ''} · ${totalAppsCount} app${
              totalAppsCount === 1 ? '' : 's'
            } hidden`
          : null,
        onToggleCollapse,
      },
    });

    // ---------- Left side: orgs + users ----------
    {
      // Center the left stack inside the block, regardless of whether the right
      // side is taller or shorter.
      let yCursor = blockTop + ((blockRows - leftRows) * ROW_HEIGHT) / 2;
      leftBlocks.forEach(({ org, users, rows }) => {
        const groupTop = yCursor;
        const groupCenter = groupTop + ((rows - 1) * ROW_HEIGHT) / 2;

        const orgNodeId = nodeId('org', org.id);
        if (!nodes.some((n) => n.id === orgNodeId)) {
          nodes.push({
            id: orgNodeId,
            type: 'org',
            position: { x: COL_X.org, y: groupCenter },
            data: {
              kind: 'org',
              label: org.name,
              sublabel: `${users.length} user${users.length === 1 ? '' : 's'}`,
            },
          });
        }
        connect(orgNodeId, clientNodeId);

        users.forEach((u: User, i: number) => {
          const uid = nodeId('user', u.id);
          if (!nodes.some((n) => n.id === uid)) {
            nodes.push({
              id: uid,
              type: 'user',
              position: { x: COL_X.user, y: groupTop + i * ROW_HEIGHT },
              data: {
                kind: 'user',
                label: u.name || u.email,
                sublabel: u.email,
              },
            });
          }
          connect(uid, orgNodeId);
        });

        yCursor += rows * ROW_HEIGHT;
      });
    }

    // ---------- Right side: forges + apps ----------
    if (!clientCollapsed && forgeAppMeta.length > 0) {
      let yCursor = blockTop + ((blockRows - rightRows) * ROW_HEIGHT) / 2;
      forgeAppMeta.forEach(({ forge: f, appsOfForge, forgeCollapsed, rows }) => {
        const groupTop = yCursor;
        const groupCenter = groupTop + ((rows - 1) * ROW_HEIGHT) / 2;
        const fid = nodeId('forge', f.forge_id);
        nodes.push({
          id: fid,
          type: 'forge',
          position: { x: COL_X.forge, y: groupCenter },
          data: {
            kind: 'forge',
            label: f.display_name || f.name,
            sublabel: f.display_name && f.display_name !== f.name ? f.name : null,
            status: f.status,
            collapsible: appsOfForge.length > 0,
            collapsed: forgeCollapsed,
            hiddenSummary: forgeCollapsed
              ? `${appsOfForge.length} app${appsOfForge.length === 1 ? '' : 's'} hidden`
              : null,
            onToggleCollapse,
          },
        });
        connect(clientNodeId, fid);

        if (!forgeCollapsed) {
          appsOfForge.forEach((a, j) => {
            const aid = nodeId('app', a.app_id);
            nodes.push({
              id: aid,
              type: 'app',
              position: { x: COL_X.app, y: groupTop + j * ROW_HEIGHT },
              data: {
                kind: 'app',
                label: a.name,
                sublabel: a.app_type,
                status: a.status,
              },
            });
            connect(fid, aid);
          });
        }

        yCursor += rows * ROW_HEIGHT;
      });
    }

    cursorY = blockTop + blockRows * ROW_HEIGHT + BLOCK_GAP;
  });

  return { nodes, edges };
};
